import type { ContentSummary } from '@augment-toolkit/core';
import { SDLC_PATH } from '../config';
import type { Crumb } from '../components/Breadcrumbs';

/**
 * Where you are in the catalog and what is above you, built from the URL.
 *
 * **The path is the hierarchy**, so the trail derives from the route parameters
 * alone and the links are the path's prefixes. Only the *names* need looking up,
 * from two caches the app already holds.
 *
 * **A level whose name has not arrived falls back to its prettified slug.** An
 * empty crumb or a suppressed trail both make navigation flicker as the catalog
 * resolves; `user-research` is not the name but is unmistakably the same thing.
 */

/** `user-research` reads as `User research` until the real name lands. */
const fromSlug = (slug: string): string =>
  slug.replace(/-/g, ' ').replace(/^./, (first) => first.toUpperCase());

const nameAt = (records: ContentSummary[], path: string, slug: string): string =>
  records.find((record) => record.path === path)?.name ?? fromSlug(slug);

export type SdlcTrail = {
  /** Root-first crumbs for the current page. Fewer than two draws nothing. */
  crumbs: Crumb[];
  /** Where "← Back to" goes, and what it calls the destination. */
  returnTo?: { to: string; label: string };
};

/**
 * The trail for one `/sdlc` route. Either cache may be empty while it loads,
 * which is what the slug fallback is for.
 */
export const buildSdlcTrail = (
  params: { phaseSlug?: string; processSlug?: string; methodSlug?: string },
  phases: ContentSummary[],
  catalog: ContentSummary[],
): SdlcTrail => {
  const { phaseSlug, processSlug, methodSlug } = params;

  if (phaseSlug === undefined) {
    return { crumbs: [] };
  }

  const phasePath = phaseSlug;
  const phaseName = nameAt(phases, phasePath, phaseSlug);
  const crumbs: Crumb[] = [{ label: phaseName, to: `${SDLC_PATH}/${phasePath}` }];

  if (processSlug === undefined) {
    return {
      crumbs,
      returnTo: { to: SDLC_PATH, label: 'the toolkit' },
    };
  }

  const processPath = `${phasePath}/${processSlug}`;
  const processName = nameAt(catalog, processPath, processSlug);
  crumbs.push({ label: processName, to: `${SDLC_PATH}/${processPath}` });

  if (methodSlug === undefined) {
    return {
      crumbs,
      returnTo: { to: `${SDLC_PATH}/${phasePath}`, label: phaseName },
    };
  }

  const methodPath = `${processPath}/${methodSlug}`;
  crumbs.push({ label: nameAt(catalog, methodPath, methodSlug) });

  return {
    crumbs,
    returnTo: { to: `${SDLC_PATH}/${processPath}`, label: processName },
  };
};

/**
 * One step of a search result's trail. **The last one carries no `to`** — it
 * names the record the card's own title already links to, and two anchors to one
 * destination with one name is a card a screen reader reads twice.
 */
export type TrailLink = { label: string; to?: string };

/**
 * The trail on a search result card. **It starts at `SDLC`**, because a result
 * has no page around it to say where the catalog begins.
 *
 * **The stage is not in it, which the designs draw and the routes do not
 * allow.** Figma's trail puts `BUILD` in it and blue — but a stage groups phases
 * and is not a level anything navigates to, so that crumb could only be blue
 * text going nowhere. The stage is already on the card, beside the reference.
 *
 * Names come from `namesByPath`, so a trail costs a lookup per level rather than
 * a scan of the catalog per level per card.
 */
export const buildRecordTrail = (
  path: string,
  names: ReadonlyMap<string, string>,
): TrailLink[] => {
  const segments = path.split('/').filter(Boolean);

  const trail: TrailLink[] = [{ label: 'SDLC', to: SDLC_PATH }];
  let prefix = '';

  for (const [index, segment] of segments.entries()) {
    prefix = prefix === '' ? segment : `${prefix}/${segment}`;
    const label = names.get(prefix) ?? fromSlug(segment);

    trail.push(
      index === segments.length - 1 ? { label } : { label, to: `${SDLC_PATH}/${prefix}` },
    );
  }

  return trail;
};

/**
 * Every record's name, keyed by its path. Built once per render of a list, so
 * the cards in it share one pass over the catalog instead of one each.
 */
export const namesByPath = (records: ContentSummary[]): Map<string, string> =>
  new Map(records.map((record) => [record.path, record.name]));

/** The same, for the numbers a dotted reference is composed from. */
export const numbersByPath = (records: ContentSummary[]): Map<string, number> =>
  new Map(records.map((record) => [record.path, record.number]));
